import { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { C, load, save, todayStr } from "./constants";
import { BARAKAH_HABITS, TIME_LABELS } from "./barakahHabits";

const BB_DAILY_KEY       = "bb_barakah_daily_v1";
const BB_DISCLAIMER_KEY  = "bb_barakah_disclaimer_shown";
const BB_REFLECTIONS_KEY = "bb_barakah_reflections_v1";
const BB_LAST_SKIP_KEY   = "bb_barakah_muhasabah_skipped";

const TYPE_COLORS = {
  prayer:     C.purple,
  dhikr:      C.green,
  quran:      C.amber,
  reflection: C.blue,
};
const TYPE_LABELS_MAP = {
  prayer:     "Prayer",
  dhikr:      "Dhikr",
  quran:      "Quran",
  reflection: "Reflection",
};

// ── HELPERS ──────────────────────────────────────────────────────

function getWeekDays() {
  const today = new Date();
  const dow = today.getDay(); // 0=Sun
  const mondayOffset = dow === 0 ? -6 : 1 - dow;
  return Array.from({ length: 7 }, (_, i) => {
    const d = new Date(today);
    d.setDate(today.getDate() + mondayOffset + i);
    return d.toISOString().slice(0, 10);
  });
}

function getTodayIndex() {
  const dow = new Date().getDay();
  return dow === 0 ? 6 : dow - 1; // Mon=0 … Sun=6
}

function computePresence(daily) {
  return getWeekDays().map(d => !!(daily[d] && daily[d].length > 0));
}

function shouldShowMuhasabah(reflections) {
  if (new Date().getDay() !== 0) return null; // only Sundays

  const lastSkipStr = load(BB_LAST_SKIP_KEY, null);
  if (lastSkipStr) {
    const days = (Date.now() - new Date(lastSkipStr).getTime()) / 86400000;
    if (days < 7) return null;
  }
  if (reflections.length > 0) {
    const days = (Date.now() - new Date(reflections[reflections.length - 1].date).getTime()) / 86400000;
    if (days < 7) return null;
  }

  const today = new Date();
  const nextSunday = new Date(today);
  nextSunday.setDate(today.getDate() + 7);
  return nextSunday.getMonth() !== today.getMonth() ? "monthly" : "weekly";
}

// ── WEEKLY PRESENCE BAR ──────────────────────────────────────────

export function WeeklyPresenceBar({ presence, todayIdx }) {
  return (
    <div style={{ display:"flex", gap:6, justifyContent:"center", marginBottom:20 }}>
      {presence.map((filled, i) => {
        const isToday = i === todayIdx;
        return (
          <div key={i} style={{
            width: isToday ? 30 : 24,
            height: isToday ? 30 : 24,
            borderRadius:"50%",
            background: filled ? "rgba(61,214,140,0.2)" : C.surface2,
            border: isToday
              ? `2px solid ${C.green}`
              : filled ? "1.5px solid rgba(61,214,140,0.4)" : `1px solid ${C.border}`,
            display:"flex", alignItems:"center", justifyContent:"center",
            transition:"all 0.2s ease",
            flexShrink:0,
          }}>
            {filled && (
              <div style={{
                width: isToday ? 10 : 8,
                height: isToday ? 10 : 8,
                borderRadius:"50%",
                background: C.green,
                opacity: 0.75,
              }} />
            )}
          </div>
        );
      })}
    </div>
  );
}

// ── DISCLAIMER MODAL ─────────────────────────────────────────────

export function BarakahDisclaimerModal({ onAccept }) {
  return createPortal(
    <div style={{
      position:"fixed", inset:0, background:"rgba(0,0,0,0.8)",
      display:"flex", alignItems:"center", justifyContent:"center",
      padding:"20px", zIndex:1000,
    }}>
      <div style={{
        background:C.surface, border:`1px solid ${C.border2}`,
        borderRadius:20, padding:"28px 24px",
        maxWidth:480, width:"100%", maxHeight:"90vh", overflowY:"auto",
      }}>
        <div style={{ fontSize:18, fontWeight:800, color:C.text, marginBottom:14 }}>
          A note before you begin
        </div>
        <div style={{ fontSize:13, color:C.text2, lineHeight:1.75, marginBottom:24 }}>
          These habits are drawn from the Quran and authenticated prophetic practice. They are general spiritual suggestions — not religious rulings, obligations, or a promise of specific financial outcomes.
          <br /><br />
          For guidance specific to your situation, please consult a qualified scholar. We are a finance app, not a fatwa service.
          <br /><br />
          May Allah put barakah in your time, your wealth, and your intentions.
        </div>
        <button onClick={onAccept} style={{
          width:"100%",
          background:`linear-gradient(135deg,${C.green},#22c55e)`,
          border:"none", borderRadius:12, color:"#0e0e13",
          fontSize:15, fontWeight:800, padding:"16px", cursor:"pointer",
        }}>
          Understood
        </button>
      </div>
    </div>,
    document.body
  );
}

// ── HABIT DETAIL SHEET ────────────────────────────────────────────

export function HabitDetailSheet({ habit, onClose }) {
  if (!habit) return null;
  const typeColor = TYPE_COLORS[habit.type] || C.text2;

  return createPortal(
    <div
      style={{
        position:"fixed", inset:0, background:"rgba(0,0,0,0.7)",
        display:"flex", alignItems:"center", justifyContent:"center",
        padding:"20px", zIndex:900,
      }}
      onClick={onClose}
    >
      <div
        style={{
          background:C.surface, border:`1px solid ${C.border2}`,
          borderRadius:20, padding:"28px 24px",
          maxWidth:480, width:"100%", maxHeight:"80vh", overflowY:"auto",
        }}
        onClick={e => e.stopPropagation()}
      >
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:16 }}>
          <div>
            <div style={{ fontSize:24, color:C.text, fontWeight:800, marginBottom:4, direction:"rtl" }}>
              {habit.arabicName}
            </div>
            <div style={{ fontSize:13, color:C.text2, fontStyle:"italic" }}>
              {habit.transliteration}
            </div>
          </div>
          <button onClick={onClose} style={{
            background:"none", border:`1px solid ${C.border}`, borderRadius:8,
            color:C.text2, fontSize:16, padding:"4px 10px", cursor:"pointer", flexShrink:0,
          }}>✕</button>
        </div>

        <div style={{
          display:"inline-block",
          background:`${typeColor}22`, border:`1px solid ${typeColor}55`,
          borderRadius:6, padding:"3px 10px", fontSize:11, fontWeight:700,
          color:typeColor, textTransform:"uppercase", letterSpacing:"0.05em",
          marginBottom:20,
        }}>
          {TYPE_LABELS_MAP[habit.type] || habit.type}
        </div>

        <div style={{ fontSize:11, color:C.text3, fontWeight:700, textTransform:"uppercase", letterSpacing:"0.07em", marginBottom:8 }}>
          What it is
        </div>
        <div style={{ fontSize:14, color:C.text2, lineHeight:1.7, marginBottom:20 }}>
          {habit.shortDescription}
        </div>

        <div style={{ fontSize:11, color:C.text3, fontWeight:700, textTransform:"uppercase", letterSpacing:"0.07em", marginBottom:8 }}>
          Why it matters
        </div>
        <div style={{ fontSize:14, color:C.text2, lineHeight:1.75, marginBottom:20 }}>
          {habit.whyItMatters}
        </div>

        <div style={{
          background:C.surface2, border:`1px solid ${C.border}`, borderRadius:10,
          padding:"10px 14px", fontSize:12, color:C.text3,
        }}>
          <span style={{ color:C.text2, fontWeight:600 }}>Source: </span>
          {habit.source}
        </div>
      </div>
    </div>,
    document.body
  );
}

// ── HABIT CARD ────────────────────────────────────────────────────

export function HabitCard({ habit, marked, onToggle, onInfo }) {
  return (
    <div
      style={{
        display:"flex", alignItems:"center", gap:12,
        padding:"13px 14px",
        background: marked ? "rgba(61,214,140,0.08)" : C.surface2,
        border:`1px solid ${marked ? "rgba(61,214,140,0.25)" : C.border}`,
        borderRadius:12, marginBottom:8, cursor:"pointer",
        transition:"all 0.2s ease",
      }}
      onClick={() => onToggle(habit.id)}
    >
      <div style={{
        width:20, height:20, borderRadius:"50%", flexShrink:0,
        background: marked ? C.green : "transparent",
        border:`1.5px solid ${marked ? C.green : C.border2}`,
        display:"flex", alignItems:"center", justifyContent:"center",
        transition:"all 0.2s ease",
      }}>
        {marked && <span style={{ fontSize:10, color:"#0e0e13", fontWeight:800, lineHeight:1 }}>✓</span>}
      </div>

      <div style={{ flex:1, minWidth:0 }}>
        <div style={{ color: marked ? C.green : C.text, fontWeight:600, fontSize:14, marginBottom:2, transition:"color 0.2s" }}>
          {habit.name}
        </div>
        <div style={{ color:C.text3, fontSize:12, lineHeight:1.4 }}>
          {habit.shortDescription}
        </div>
      </div>

      <button
        onClick={e => { e.stopPropagation(); onInfo(habit); }}
        style={{
          background:C.surface3, border:`1px solid ${C.border2}`,
          borderRadius:20, color:C.text2, fontSize:11, fontWeight:700,
          cursor:"pointer", padding:"4px 10px", flexShrink:0,
          lineHeight:1.4, letterSpacing:"0.01em",
        }}
        aria-label={`About ${habit.name}`}
      >
        info
      </button>
    </div>
  );
}

// ── MUHASABAH PROMPT ──────────────────────────────────────────────

export function MuhasabahPrompt({ type, onSave, onSkip }) {
  const [text, setText] = useState("");
  const isMonthly = type === "monthly";
  const title   = isMonthly ? "End of month muhasabah" : "Weekly muhasabah";
  const prompt  = isMonthly
    ? "How was your ibadah this month? What do you want to carry forward into the next?"
    : "How was your ibadah this week? What do you want to carry forward?";

  return createPortal(
    <div style={{
      position:"fixed", inset:0, background:"rgba(0,0,0,0.8)",
      display:"flex", alignItems:"center", justifyContent:"center",
      padding:"20px", zIndex:900,
    }}>
      <div style={{
        background:C.surface, border:`1px solid ${C.border2}`,
        borderRadius:20, padding:"28px 24px",
        maxWidth:480, width:"100%",
      }}>
        <div style={{ fontSize:16, fontWeight:800, color:C.text, marginBottom:6 }}>{title}</div>
        <div style={{ fontSize:13, color:C.text2, lineHeight:1.65, marginBottom:16 }}>{prompt}</div>
        <textarea
          value={text}
          onChange={e => setText(e.target.value)}
          placeholder="Write a few lines…"
          rows={5}
          style={{
            width:"100%", background:C.surface2, border:`1px solid ${C.border2}`,
            borderRadius:10, padding:"12px 14px", color:C.text, fontSize:13,
            fontFamily:"'DM Sans',sans-serif", outline:"none", resize:"none",
            boxSizing:"border-box", lineHeight:1.6, marginBottom:6,
          }}
        />
        <div style={{ fontSize:11, color:C.text3, marginBottom:16 }}>
          Saved privately on your device — not shared anywhere
        </div>
        <div style={{ display:"flex", gap:10 }}>
          <button onClick={() => onSave(text.trim())} style={{
            flex:1, background:`linear-gradient(135deg,${C.green},#22c55e)`,
            border:"none", borderRadius:10, color:"#0e0e13",
            fontSize:14, fontWeight:700, padding:"13px", cursor:"pointer",
          }}>
            Save privately
          </button>
          <button onClick={onSkip} style={{
            flex:1, background:C.surface2, border:`1px solid ${C.border}`,
            borderRadius:10, color:C.text2, fontSize:14, fontWeight:600,
            padding:"13px", cursor:"pointer",
          }}>
            {isMonthly ? "Skip this month" : "Skip this week"}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

// ── ONBOARDING OPT-IN CARD ────────────────────────────────────────

export function BarakahOnboardingCard({ onEnable, onSkip }) {
  return (
    <div style={{ animation:"fadeUp 0.35s ease", textAlign:"center" }}>
      <div style={{ fontSize:48, marginBottom:16 }}>🌙</div>
      <div style={{ fontSize:22, fontWeight:800, color:C.text, letterSpacing:"-0.03em",
        lineHeight:1.25, marginBottom:10 }}>
        Would you like a Barakah habits reminder?
      </div>
      <div style={{ fontSize:13, color:C.text2, lineHeight:1.7, marginBottom:24, padding:"0 8px" }}>
        An optional spiritual checklist — tahajjud, adhkar, duha, and muhasabah — for those who want to pair their financial intentions with daily practice.
        <br /><br />
        Grounded in authentic Islamic sources. Completely optional. Off by default.
      </div>
      <div style={{
        background:C.surface2, border:`1px solid ${C.border}`,
        borderRadius:14, padding:"14px 16px", marginBottom:28, textAlign:"left",
      }}>
        <div style={{ display:"flex", gap:8, flexWrap:"wrap" }}>
          {["Tahajjud","Morning adhkar","Istighfar","Duha","Evening adhkar","Muhasabah"].map(h => (
            <span key={h} style={{
              background:C.surface3, border:`1px solid ${C.border}`,
              borderRadius:6, padding:"3px 9px", fontSize:11, color:C.text3,
            }}>{h}</span>
          ))}
        </div>
      </div>
      <button onClick={onEnable} style={{
        width:"100%", background:`linear-gradient(135deg,${C.green},#22c55e)`,
        border:"none", borderRadius:14, color:"#0e0e13",
        fontSize:15, fontWeight:800, padding:"16px", cursor:"pointer",
        marginBottom:12, boxShadow:"0 4px 16px rgba(61,214,140,0.25)",
      }}>
        Yes, add it
      </button>
      <button onClick={onSkip} style={{
        width:"100%", background:"none", border:"none",
        color:C.text3, fontSize:13, padding:"8px", cursor:"pointer",
      }}>
        Maybe later
      </button>
    </div>
  );
}

// ── UNLOCK CARD (feature off) ─────────────────────────────────────

function UnlockCard({ onEnable }) {
  return (
    <div style={{ textAlign:"center", padding:"40px 16px" }}>
      <div style={{ fontSize:48, marginBottom:16 }}>🌙</div>
      <div style={{ fontSize:17, fontWeight:700, color:C.text, marginBottom:10, lineHeight:1.35 }}>
        Add a spiritual layer to your financial intentions
      </div>
      <div style={{ fontSize:13, color:C.text2, lineHeight:1.7, marginBottom:28, maxWidth:300, margin:"0 auto 28px" }}>
        An optional checklist of daily spiritual habits — grounded in authentic Islamic sources.
        No scores, no streaks. Just a gentle anchor alongside your finances.
      </div>
      <button onClick={onEnable} style={{
        background:`linear-gradient(135deg,${C.green},#22c55e)`,
        border:"none", borderRadius:12, color:"#0e0e13",
        fontSize:14, fontWeight:700, padding:"14px 28px",
        cursor:"pointer", boxShadow:"0 4px 16px rgba(61,214,140,0.25)",
      }}>
        Enable Barakah Habits
      </button>
    </div>
  );
}

// ── MAIN TAB ──────────────────────────────────────────────────────

export function BarakahHabitsTab({ enabled, onEnable }) {
  const [todayMarked, setTodayMarked]         = useState([]);
  const [presence, setPresence]               = useState(Array(7).fill(false));
  const [showDisclaimer, setShowDisclaimer]   = useState(false);
  const [showAbout, setShowAbout]             = useState(false);
  const [activeHabit, setActiveHabit]         = useState(null);
  const [muhasabah, setMuhasabah]             = useState(null);
  const [toast, setToast]                     = useState(null);
  const [welcomeBack, setWelcomeBack]         = useState(false);
  const [reflections, setReflections]         = useState([]);

  const todayIdx = getTodayIndex();

  useEffect(() => {
    if (!enabled) return;

    const daily = load(BB_DAILY_KEY, {});
    const today = todayStr();
    setTodayMarked(daily[today] || []);
    setPresence(computePresence(daily));

    const refs = load(BB_REFLECTIONS_KEY, []);
    setReflections(refs);

    // Welcome back after 7+ days away
    const daysWithEntries = Object.keys(daily).filter(d => daily[d]?.length > 0).sort();
    if (daysWithEntries.length > 0) {
      const lastDay = new Date(daysWithEntries[daysWithEntries.length - 1] + "T12:00:00");
      if ((Date.now() - lastDay.getTime()) / 86400000 >= 7) setWelcomeBack(true);
    }

    if (!load(BB_DISCLAIMER_KEY, null)) setShowDisclaimer(true);

    const promptType = shouldShowMuhasabah(refs);
    if (promptType) setMuhasabah(promptType);
  }, [enabled]);

  const handleToggle = (id) => {
    const daily = load(BB_DAILY_KEY, {});
    const today = todayStr();
    const current = daily[today] || [];
    const wasMarked = current.includes(id);
    daily[today] = wasMarked ? current.filter(x => x !== id) : [...current, id];
    save(BB_DAILY_KEY, daily);
    setTodayMarked(daily[today]);
    setPresence(computePresence(daily));
    setWelcomeBack(false);
    if (!wasMarked) {
      setToast("Marked. May Allah accept it. 🤲");
      setTimeout(() => setToast(null), 2500);
    }
  };

  const handleAcceptDisclaimer = () => {
    save(BB_DISCLAIMER_KEY, "true");
    setShowDisclaimer(false);
  };

  const handleSaveReflection = (text) => {
    if (!text) { handleSkipMuhasabah(); return; }
    const entry = { date: new Date().toISOString(), text, type: muhasabah };
    const updated = [...reflections, entry];
    save(BB_REFLECTIONS_KEY, updated);
    setReflections(updated);
    setMuhasabah(null);
  };

  const handleSkipMuhasabah = () => {
    save(BB_LAST_SKIP_KEY, new Date().toISOString());
    setMuhasabah(null);
  };

  if (!enabled) return <UnlockCard onEnable={onEnable} />;

  const grouped = Object.entries(TIME_LABELS).map(([key, meta]) => ({
    key, ...meta,
    habits: BARAKAH_HABITS.filter(h => h.timeOfDay === key),
  })).filter(g => g.habits.length > 0);

  return (
    <>
      {/* One-time disclaimer */}
      {showDisclaimer && <BarakahDisclaimerModal onAccept={handleAcceptDisclaimer} />}

      {/* "About this feature" re-open */}
      {showAbout && <BarakahDisclaimerModal onAccept={() => setShowAbout(false)} />}

      {/* Habit detail sheet */}
      {activeHabit && <HabitDetailSheet habit={activeHabit} onClose={() => setActiveHabit(null)} />}

      {/* Muhasabah modal */}
      {muhasabah && (
        <MuhasabahPrompt type={muhasabah} onSave={handleSaveReflection} onSkip={handleSkipMuhasabah} />
      )}

      {/* Toast */}
      {toast && (
        <div style={{
          position:"fixed", bottom:110, left:"50%", transform:"translateX(-50%)",
          background:C.surface3, border:"1px solid rgba(61,214,140,0.3)",
          borderRadius:10, padding:"10px 20px", fontSize:14, color:C.green,
          fontWeight:600, zIndex:800, whiteSpace:"nowrap",
          boxShadow:"0 4px 16px rgba(0,0,0,0.4)", pointerEvents:"none",
        }}>
          {toast}
        </div>
      )}

      {/* Header */}
      <div style={{ marginBottom:6 }}>
        <div style={{ fontSize:16, fontWeight:800, color:C.text, marginBottom:6 }}>Barakah Habits</div>
        <div style={{ fontSize:12, color:C.text2, lineHeight:1.65, marginBottom:20 }}>
          Rizq comes from Allah alone. These are practices that scholars have connected to barakah — in provision, peace of mind, and direction. An invitation, not a formula.
        </div>
      </div>

      {/* Weekly presence bar */}
      <WeeklyPresenceBar presence={presence} todayIdx={todayIdx} />

      {/* Welcome back */}
      {welcomeBack && (
        <div style={{
          background:C.surface2, border:`1px solid ${C.border}`, borderRadius:12,
          padding:"12px 16px", marginBottom:16, textAlign:"center",
          fontSize:13, color:C.text2,
        }}>
          Welcome back. Start where you are — that's all that's asked.
        </div>
      )}

      {/* Empty state (first time, no marks today) */}
      {todayMarked.length === 0 && !welcomeBack && (
        <div style={{
          background:C.surface2, border:`1px solid ${C.border}`, borderRadius:12,
          padding:"12px 16px", marginBottom:16, textAlign:"center",
          fontSize:12, color:C.text3, lineHeight:1.65,
        }}>
          Tap any habit to mark it for today.
          <br />Come back when you can — no pressure, no streak to protect.
        </div>
      )}

      {/* Habit groups */}
      {grouped.map(group => (
        <div key={group.key} style={{ marginBottom:20 }}>
          <div style={{
            fontSize:11, fontWeight:700, color:C.text3,
            textTransform:"uppercase", letterSpacing:"0.08em",
            marginBottom:10, display:"flex", alignItems:"center", gap:6,
          }}>
            <span>{group.icon}</span>
            <span>{group.label}</span>
          </div>
          {group.habits.map(habit => (
            <HabitCard
              key={habit.id}
              habit={habit}
              marked={todayMarked.includes(habit.id)}
              onToggle={handleToggle}
              onInfo={setActiveHabit}
            />
          ))}
        </div>
      ))}

      {/* Footer */}
      <div style={{ textAlign:"center", marginTop:8, paddingBottom:4 }}>
        <button
          onClick={() => setShowAbout(true)}
          style={{ background:"none", border:"none", color:C.text3, fontSize:11, cursor:"pointer", textDecoration:"underline" }}
        >
          About this feature
        </button>
      </div>
    </>
  );
}
